import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRecords, createRecord, deleteRecord } from '../api';
import { enqueueRecord, getQueueCount, flushQueue } from '../offlineQueue';

const RecordPage = () => {
  const { bodyPart, exercise } = useParams();
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];

  const weightKey = `replog_weight_${exercise}`;
  const repsKey = `replog_reps_${exercise}`;

  const [sets, setSets] = useState([]);
  const [weight, setWeight] = useState(() => localStorage.getItem(weightKey) ?? '');
  const [reps, setReps] = useState(() => localStorage.getItem(repsKey) ?? '');
  const [date, setDate] = useState(today);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineCount, setOfflineCount] = useState(getQueueCount());
  const [syncMessage, setSyncMessage] = useState('');

  // オンライン・オフライン状態の監視
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      const count = await flushQueue(createRecord);
      if (count > 0) {
        setSyncMessage(`オフライン中の${count}件を同期しました`);
        setOfflineCount(0);
        fetchSets(date);
        setTimeout(() => setSyncMessage(''), 4000);
      }
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [date]);

  // 記録の取得
  const fetchSets = useCallback((targetDate) => {
    setLoading(true);
    getRecords({ date: targetDate, exercise })
      .then((res) => setSets(res.data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [exercise]);

  // 日付変更時に記録を再取得
  useEffect(() => {
    fetchSets(date);
  }, [date, fetchSets]);

  const handleAdd = async () => {
    // 入力チェック（重量は0も有効とするため、空文字とnull/undefinedのみエラー）
    if (weight === '' || weight === null || weight === undefined || !reps) return alert('重量とレップ数を入力してください');

    const newRecord = {
      date,
      bodyPart,
      exercise,
      setNumber: sets.length + 1,
      weight: parseFloat(weight),
      reps: parseInt(reps),
    };

    if (isOnline) {
      // オンライン：即時登録
      try {
        const res = await createRecord(newRecord);
        setSets([...sets, res.data]);
      } catch (err) {
        console.error(err);
        alert('登録に失敗しました');
      }
    } else {
      // オフライン：キューに保存
      enqueueRecord(newRecord);
      const tempRecord = { ...newRecord, recordId: `offline-${Date.now()}` };
      setSets([...sets, tempRecord]);
      setOfflineCount(getQueueCount());
    }
  };

  const handleDelete = async (recordId) => {
    if (recordId.startsWith('offline-')) {
      setSets(sets.filter((s) => s.recordId !== recordId));
      return;
    }
    try {
      await deleteRecord(recordId);
      setSets(sets.filter((s) => s.recordId !== recordId));
    } catch (err) {
      console.error(err);
      alert('削除に失敗しました');
    }
  };

  if (loading) return <div className="loading">読み込み中...</div>;

  return (
    <div className="page">
      <h1>{exercise}</h1>

      {/* パンくずリスト */}
      <div className="breadcrumb">
        <button className="back-button" onClick={() => navigate('/')}>部位選択</button>
        <span> &gt; </span>
        <button className="back-button" onClick={() => navigate(`/exercises/${bodyPart}`)}>{bodyPart}</button>
        <span> &gt; {exercise}</span>
      </div>

      {/* オフライン通知バナー */}
      {!isOnline && (
        <div className="offline-banner">
          📵 オフラインモード｜記録はオンライン復帰時に自動同期されます
          {offlineCount > 0 && <span>（未同期: {offlineCount}件）</span>}
        </div>
      )}

      {/* 同期完了メッセージ */}
      {syncMessage && (
        <div className="sync-banner">✅ {syncMessage}</div>
      )}

      {/* 日付ピッカー */}
      <div className="date-picker">
        <label>実施日：</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      {/* セット入力フォーム */}
      <div className="input-form">
        <h2>セットを追加</h2>
        <div className="input-row">
          <label>重量 (kg)</label>
          <input
            type="number"
            value={weight}
            onChange={(e) => { setWeight(e.target.value); localStorage.setItem(weightKey, e.target.value); }}
            placeholder="0"
            step="0.5"
          />
        </div>
        <div className="input-row">
          <label>レップ数</label>
          <div className="reps-stepper">
            <button className="stepper-button" onClick={() => setReps(v => { const n = String(Math.max(0, (parseInt(v) || 0) - 1)); localStorage.setItem(repsKey, n); return n; })}>ー</button>
            <input
              type="number"
              value={reps}
              onChange={(e) => { setReps(e.target.value); localStorage.setItem(repsKey, e.target.value); }}
              placeholder="0"
            />
            <button className="stepper-button" onClick={() => setReps(v => { const n = String((parseInt(v) || 0) + 1); localStorage.setItem(repsKey, n); return n; })}>＋</button>
          </div>
        </div>
        <button className="primary-button" onClick={handleAdd}>
          {isOnline ? '登録' : '登録（オフライン保存）'}
        </button>
      </div>

      {/* 記録済みセット一覧 */}
      <div className="sets-list">
        <h2>記録済みセット</h2>
        {sets.length === 0 ? (
          <p>まだ記録がありません</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>セット</th>
                <th>重量 (kg)</th>
                <th>レップ数</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sets
                .sort((a, b) => a.setNumber - b.setNumber)
                .map((set) => (
                  <tr key={set.recordId} className={set.recordId?.startsWith('offline-') ? 'offline-row' : ''}>
                    <td>{set.setNumber}</td>
                    <td>{set.weight}</td>
                    <td>{set.reps}</td>
                    <td>
                      <button className="delete-button" onClick={() => handleDelete(set.recordId)}>
                        削除
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RecordPage;